<template>
  <main class="linkcheck">
    <!-- Header -->
    <!-- would be nice if the loading flag had a bearing on the tool too -->
    <section class="card card--hero">
      <div class="hero">
        <div class="hero__text">
          <h1>LinkCheck</h1>
          <p class="sub">
            Answer: “Was this transaction related to this address?” — binary verdict with evidence.
          </p>
        </div>
        <div class="status-right">
          <span class="pill" :class="{'pill--ok': linkcheckCredits > 0, 'pill--warn': linkcheckCredits === 0}">
            LinkCheck credits: <strong>{{ linkcheckCreditsDisplay }}</strong>
          </span>
        </div>
      </div>
    </section>

    <!-- Notifications -->
<!-- Notifications -->
<transition name="slide-fade">
  <section
    v-if="notificationMessage"
    class="card notif"
    :class="`notif--${notificationType}`"
    role="alert"
    aria-live="polite"
  >
    <p class="notif__text">{{ notificationMessage }}</p>

    <button
      class="notif__close"
      @click="dismissNotif"
      aria-label="Dismiss notification"
      title="Dismiss"
    >
      &times;
    </button>
  </section>
</transition>

<!-- Form -->
<section class="tool-card">
  <h2>Check a Link</h2>

  <form @submit.prevent="submitLinkCheck" class="linkcheck-form">
    <div class="form-grid-2">
      <!-- TX HASH -->
      <div class="field field--xl field--mono field--hash">
        <label for="tx">
          Transaction Hash
        </label>

        <div class="input-affix">
          <!-- prefix icon -->
          <svg class="prefix-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 7h10v10H7z" fill="currentColor" opacity=".35"/>
            <path d="M4 4h16v16H4z" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".85"/>
          </svg>

          <input
            id="tx"
            class="input input--affixed"
            v-model.trim="txHash"
            :disabled="isSubmittingRequest"
            placeholder="0xdf158166d… (ETH) or 71a6c9… (BTC)"
            autocomplete="off"
            data-1p-ignore
            autocapitalize="off"
            spellcheck="false"
            inputmode="text"
            maxlength="150"
            aria-label="Transaction Hash"
            aria-describedby="tx-hint"

            autofocus
          />

        
        </div>

        <p id="tx-hint" class="hint">
          Paste full hash here
        </p>
        <p id="tx-hint" class="hint">
          ETH hashes need to start with 0x
        </p>
      </div>

      <!-- TO ADDRESS -->
      <div class="field field--xl field--mono field--addr">
        <label for="addr">
          To Address <span class="muted">(optional)</span>
        </label>

        <div class="input-affix">
          <svg class="prefix-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12h14" stroke="currentColor" stroke-width="1.7" opacity=".9"/>
            <circle cx="6.5" cy="12" r="3.5" fill="currentColor" opacity=".25"/>
            <circle cx="17.5" cy="12" r="3.5" fill="currentColor" opacity=".25"/>
          </svg>

          <input
            id="addr"
            class="input input--affixed"
            v-model.trim="toAddress"
            :disabled="isSubmittingRequest"
            placeholder="Optional: 0x… or BTC address"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            data-1p-ignore
            inputmode="text"
            maxlength="150"
            aria-label="To Address"
            aria-describedby="addr-hint"
          />

          <button
            v-if="toAddress"
            type="button"
            class="suffix-btn"
            @click="toAddress=''"
            title="Clear"
          >
            Clear
          </button>
        </div>

        <p id="addr-hint" class="hint">
          Helpful for multi-output txns or token transfers.
        </p>
      </div>
    </div>
        <!-- Options -->
        <details class="options" :open="optionsOpen" v-if="useOptions">
          <summary @click.prevent="optionsOpen = !optionsOpen">
            Advanced options
          </summary>
          <div class="options-grid">
            <div class="field small">
              <label>Max hops</label>
              <input type="number" v-model.number="params.maxHops" min="1" max="12" :disabled="isSubmittingRequest" />
            </div>
            <div class="field small">
              <label>Min USD continuity</label>
              <input type="number" v-model.number="params.minUsdContinuity" min="0" step="1" :disabled="isSubmittingRequest" />
            </div>
            <div class="field">
              <label>Time window (from)</label>
              <input type="date" v-model="params.timeFrom" :disabled="isSubmittingRequest" />
            </div>
            <div class="field">
              <label>Time window (to)</label>
              <input type="date" v-model="params.timeTo" :disabled="isSubmittingRequest" />
            </div>
    
          </div>
        </details>

        <div class="actions">
          <button
            class="primary-button"
            type="submit"
            :disabled="!canSubmit || isSubmittingRequest"
            data-track="linkcheck_submit"
          >
            {{ isSubmittingRequest ? 'Submitting…' : 'Run LinkCheck' }}
          </button>
          <button class="text-button" type="button" @click="resetForm" :disabled="isSubmittingRequest">Reset</button>
         </div>
      </form>
    </section>
<!-- Recent requests -->
<section class="tool-card" style="margin-top:16px">
  <div class="flex-between">
    <h2 style="margin:0">My LinkCheck requests</h2>
    <div class="controls">
      <!-- Toggle: show only LinkCheck (if you didn't add mode_only on backend) -->
      <label class="tiny" style="margin-right:8px">
        <input type="checkbox" v-model="onlyLinkcheck" @change="fetchRequestsLC" />

        LinkCheck only
      </label>

      <button class="text-button" @click="fetchRequestsLC" :disabled="reqLoading">Refresh</button>
    </div>
  </div>

  <p v-if="reqError" class="callout callout--warn">{{ reqError }}</p>

  <div v-if="reqLoading" class="muted small">Loading…</div>

  <div v-else>
    <table v-if="visibleRequests.length" class="ph-table">
      <thead>
        <tr>
          <th style="width:160px">Time</th>
          <th>Tx</th>
          <th>To</th>
          <th style="width:110px">Mode</th>
          <th style="width:110px">Status</th><!-- add a Verdict column -->
<th style="width:110px">Verdict</th>
<th style="width:140px">Actions</th>

        </tr>
      </thead>
      <tbody>
        <tr
          v-for="r in visibleRequests"
          :key="r.request_id"
          :class="{ 'row-dim': r.mode !== 'linkcheck' }"
        >
          <td class="nowrap">{{ formatDateTidy(r.request_time) }}</td>
<td class="mono txcell">
  <span class="truncate" :title="r.transaction_hash">{{ r.transaction_hash }}</span>
  <span class="copy-group">
    <button class="copy-btn tiny"
            @click="copyField(r.request_id, 'tx', r.transaction_hash)">
      Copy
    </button>
    <transition name="fade">
      <span v-if="isCopied(r.request_id, 'tx')" class="copy-inline" aria-live="polite">
        Copied!
      </span>
    </transition>
  </span>
</td>

<td class="mono txcell">
  <template v-if="r.to_address">
    <span class="truncate" :title="r.to_address">{{ r.to_address }}</span>
    <span class="copy-group">
      <button class="copy-btn tiny"
              @click="copyField(r.request_id, 'to', r.to_address)">
        Copy
      </button>
      <transition name="fade">
        <span v-if="isCopied(r.request_id, 'to')" class="copy-inline" aria-live="polite">
          Copied!
        </span>
      </transition>
    </span>
  </template>
  <span v-else>—</span>
</td>

       <td>
  <span
    class="pill"
    :class="r.mode === 'linkcheck' ? 'pill--ok' : ''"
    :title="`internal: ${r.mode || 'null'}`"
  >
    {{ displayMode(r.mode) }}
  </span>
</td>

          <td>{{ r.status }}</td>
          <td>
  <template v-if="r.status === 'complete' && getLcVerdict(r) !== null">
    <span class="pill"
          :class="verdictPillClass(getLcVerdict(r))"
          :title="verdictTitle(r)">
      {{ getLcVerdict(r) }}
    </span>
  </template>
  <template v-else>
    <span class="muted">—</span>
  </template>
</td>

<td>
  <div class="row-actions">
    <!-- Show View only for completed LinkCheck requests with a valid result -->
    <template v-if="canOpenReport(r)">
      <button class="btn btn--ghost tiny" @click="openReportLC(r)">View</button>
    </template>

    <!-- Delete controls (unchanged) -->
    <template v-if="confirmDelete[r.request_id]">
      <button class="btn btn--danger tiny"
              @click="deleteRequestLC(r.request_id)"
              :disabled="deletingIds[r.request_id]">
        <span v-if="deletingIds[r.request_id]">Deleting…</span>
        <span v-else>Confirm</span>
      </button>
      <button class="btn btn--ghost tiny"
              @click="cancelDeleteLC(r.request_id)"
              :disabled="deletingIds[r.request_id]">
        Cancel
      </button>
    </template>
    <template v-else>
      <button class="btn btn--ghost tiny"
              @click="askDeleteLC(r.request_id)"
              :disabled="deletingIds[r.request_id]"
              title="Delete this request">
        Delete
      </button>
    </template>
  </div>
</td>

   
        </tr>
      </tbody>
    </table>

    <p v-else class="muted">No requests found.</p>

    <div v-if="totalReq > reqLimit" class="ph-pagination">
      <button class="btn" @click="prevReqPage" :disabled="reqPage === 1">Prev</button>
      <span class="muted small">Page {{ reqPage }} of {{ Math.ceil(totalReq / reqLimit) }}</span>
      <button class="btn" @click="nextReqPage" :disabled="reqPage * reqLimit >= totalReq">Next</button>
    </div>
  </div>
</section>

 
      <!-- To‑address picker modal -->
      <div v-if="showToAddressPicker" class="panelmodal">
        <div class="panelmodal__head">
          <strong>Select a valid To Address</strong>
          <button @click="showToAddressPicker = false" class="close">×</button>
        </div>
        <div class="panelmodal__body">
      <p
  v-for="entry in toAddressSuggestions"
  :key="entry.address"
  class="choice"
  @click="chooseToAddress(entry.address)"
  data-track="tool_to_address_pick"
  :data-track-props="JSON.stringify({
    has_amt: !!entry.amount || !!entry.value,
    token: entry.token || null
  })"
>
  {{ entry.address }}
            <span v-if="entry.amount && entry.token && isRecognizedToken(entry.token)">
              — {{ formatAmount(entry.amount, tokenSymbols?.[entry.token?.toLowerCase?.()] || entry.token) }}
            </span>
            <span v-else-if="entry.value">
              — {{ formatValue(entry.value, entry.network || 'ethereum') }}
            </span>
          </p>
        </div>
      </div>

      <!-- Address help (transaction picker) -->
      <div v-if="showAddressHelpPanel" class="panelmodal">
        <div class="panelmodal__head">
          <strong>Select the transaction you sent</strong>
          <button @click="showAddressHelpPanel = false" class="close">×</button>
        </div>
        <div class="panelmodal__body">
          <div v-for="tx in paginatedResults" :key="tx.hash" class="tx">
            <div class="tx__summary">
              <strong>{{ tx.amount }}</strong> received at {{ formatDateLocal(tx.timestamp) }}<br />
              From:
              <span class="short" :title="tx.from">{{ shorten(tx.from) }}</span>
            </div>
           <button
  class="btn btn--primary btn--sm"
  @click="selectSuggestedTransaction(tx)"
  data-track="tool_pick_suggested_tx"
  :data-track-props="JSON.stringify({
    has_amt: !!tx.amount,
    from_short: !!tx.from
  })"
>
  Select
</button>
          </div>

          <div class="muted tiny center space-top">
            Showing {{ resultsPerPageInAddressHelpPanel }} per page.
            {{ addressHelpResults.length >= 50
              ? 'Showing a maximum of 50 results. There may be more transactions not shown.'
              : `Total results: ${addressHelpResults.length}.` }}
          </div>

          <div class="pager">
          <button
  class="btn btn--ghost"
  @click="currentPageInAddressHelpPanel--"
  :disabled="currentPageInAddressHelpPanel === 1"
  data-track="tool_addrhelp_prev"
  :data-track-props="JSON.stringify({ page: currentPageInAddressHelpPanel - 1 })"
>‹ Prev</button>
            <span class="muted tiny">{{ currentPageInAddressHelpPanel }} / {{ totalPagesInAddressHelpPanel }}</span>
          <button
  class="btn btn--ghost"
  @click="currentPageInAddressHelpPanel++"
  :disabled="currentPageInAddressHelpPanel >= totalPagesInAddressHelpPanel"
  data-track="tool_addrhelp_next"
  :data-track-props="JSON.stringify({ page: currentPageInAddressHelpPanel + 1 })"
>Next ›</button>
          </div>

          <div v-if="addressHelpResults.length >= 50" class="tiny center space-top">
            Not seeing it?
            <a :href="getExplorerUrlForAddress(addressUsedForAssistance, inferredNetwork)" target="_blank" rel="noopener noreferrer">
              View full history on {{ inferredNetwork && inferredNetwork.charAt(0).toUpperCase() + inferredNetwork.slice(1) }} →
            </a>
          </div>
        </div>
      </div>
<teleport to="body">
  <div v-if="showReportModalLC" class="report-overlay" @click.self="closeReportLC">
    <div class="modal modal--report" role="dialog" aria-modal="true" aria-label="LinkCheck report">
      <div class="modal__head">
        <strong>LinkCheck Report</strong>
        <button class="close" @click="closeReportLC" aria-label="Close">×</button>
      </div>
<div class="modal__body" v-if="reportRequestLC && reportObjLC">
  <div class="lc-report">
    <!-- HERO: Verdict + Confidence -->
    <section class="report__hero" :class="confidenceLevelClass">
      <div class="hero-left">
  <div class="hero-line">
    <span class="pill" :class="verdictPillClass(reportObjLC.verdict)">
      {{ reportObjLC.verdict }}
    </span>
    <span class="mode-badge">{{ displayMode(reportRequestLC.mode) }}</span>
  </div>

  <div class="hero-note">{{ verdictExplainer }}</div>

  <div class="hero-sub mono">
    Request #{{ reportRequestLC.request_id }} • {{ reportLocalTime(reportRequestLC.request_time) }}
  </div>
</div>


      <div class="hero-right">
        <div class="conf-score">{{ confidencePct }}%</div>
        <div class="meter" aria-label="Confidence">
          <div class="meter__fill" :style="{ width: confidencePct + '%' }"></div>
        </div>
        <div class="conf-caption">confidence</div>
      </div>
    </section>

    <!-- META GRID -->
    <section class="meta-grid">
      <div>
        <label>Transaction</label>
        <div class="value mono copywrap">
          <span class="truncate" :title="reportRequestLC.transaction_hash">{{ reportRequestLC.transaction_hash }}</span>
          <button class="copy-link" @click="copyText(reportRequestLC.transaction_hash, 'tx')">Copy</button>
          <span v-if="isCopiedKey('tx')" class="copied-ok">Copied</span>
        </div>
      </div>

      <div v-if="reportRequestLC.to_address">
        <label>To</label>
        <div class="value mono copywrap">
          <span class="truncate" :title="reportRequestLC.to_address">{{ reportRequestLC.to_address }}</span>
          <button class="copy-link" @click="copyText(reportRequestLC.to_address, 'to')">Copy</button>
          <span v-if="isCopiedKey('to')" class="copied-ok">Copied</span>
        </div>
      </div>

      <div>
        <label>Chains scanned</label>
        <div class="value">{{ (reportObjLC.chains || []).join(', ') || '—' }}</div>
      </div>

  <div v-if="reportObjLC.timeWindow">
  <label>Time window</label>
  <div class="value">
    {{ reportObjLC.timeWindow.from || '—' }} → {{ reportObjLC.timeWindow.to || '—' }}
    <div
      v-if="reportObjLC.timeWindow.from && reportObjLC.timeWindow.to"
      class="tiny muted"
      style="margin-top:2px"
    >
      Duration: {{ humanTimeWindow(reportObjLC.timeWindow.from, reportObjLC.timeWindow.to) }}
    </div>
  </div>
</div>

    </section>

    <!-- STATS -->
    <section class="stat-cards">
      <div class="stat">
        <div class="stat__num">{{ reportObjLC.counts?.hopsAssessed ?? 0 }}</div>
        <div class="stat__label">Hops assessed</div>
      </div>
      <div class="stat">
        <div class="stat__num">{{ reportObjLC.counts?.ethTx ?? 0 }}</div>
        <div class="stat__label">ETH or TRON tx</div>
      </div>
      <div class="stat">
        <div class="stat__num">{{ reportObjLC.counts?.btcTx ?? 0 }}</div>
        <div class="stat__label">BTC tx</div>
      </div>
      <div class="stat stat--quality">
        <div class="stat__label">Scan quality</div>
        <div class="chips">
          <span class="chip" :class="{'chip--warn': (reportObjLC.quality?.warnings ?? 0)>0}">
            warnings: {{ reportObjLC.quality?.warnings ?? 0 }}
          </span>
          <span class="chip" :class="{'chip--warn': (reportObjLC.quality?.overflows ?? 0)>0}">
            overflows: {{ reportObjLC.quality?.overflows ?? 0 }}
          </span>
          <span class="chip" :class="{'chip--warn': (reportObjLC.quality?.exceptions ?? 0)>0}">
            exceptions: {{ reportObjLC.quality?.exceptions ?? 0 }}
          </span>
          <span class="chip" :class="{'chip--err': (reportObjLC.quality?.errors ?? 0)>0}">
            errors: {{ reportObjLC.quality?.errors ?? 0 }}
          </span>
        </div>
      </div>
    </section>

    <!-- SAMPLES -->
    <section v-if="(reportObjLC.ethSamples || []).length" class="sample">
      <h4>ETH transactions (sample)</h4>
      <table class="ph-table small">
        <thead>
          <tr>
            <th>Hash</th><th>From</th><th>To</th><th>Amount</th><th>Token</th><th>Time</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(t, i) in reportObjLC.ethSamples" :key="'e'+i">
            <td class="mono">{{ shorten(t.hash) }}</td>
            <td class="mono">{{ shorten(t.from) }}</td>
            <td class="mono">{{ shorten(t.to) }}</td>
            <td class="mono">{{ t.amount }}</td>
            <td>{{ t.token || '—' }}</td>
            <td>{{ t.timestamp }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-if="(reportObjLC.btcSamples || []).length" class="sample">
      <h4>BTC transactions (sample)</h4>
      <table class="ph-table small">
        <thead>
          <tr>
            <th>Hash</th><th>From</th><th>To</th><th>Fee (sats)</th><th>Time</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(t, i) in reportObjLC.btcSamples" :key="'b'+i">
            <td class="mono">{{ shorten(t.hash) }}</td>
            <td class="mono">{{ t.from ? shorten(t.from) : '—' }}</td>
            <td class="mono">{{ t.to ? shorten(t.to) : '—' }}</td>
            <td class="mono">{{ t.fee ?? '—' }}</td>
            <td>{{ t.timestamp }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- RAW -->
    <details class="raw-report" style="margin-top:12px">
      <summary>Raw JSON</summary>
      <pre class="mono small-pre">{{ pretty(reportObjLC) }}</pre>
    </details>
  </div>
</div>

    </div>
  </div>
</teleport>
<!-- Credit confirmation modal for LinkCheck -->
<teleport to="body">
  <div
    v-if="showCreditCostModalLC"
    class="report-overlay credit-modal-overlay"
    role="dialog"
    aria-modal="true"
    @click.self="closeCreditModalLC"
  >
    <div class="modal credit-modal" role="document">
      <header class="modal__head">
        <strong>Confirm LinkCheck Credit Use</strong>
        <button
          class="close"
          @click="closeCreditModalLC"
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <section class="modal__body">
        <p v-if="creditModalBusyLC" class="muted">
          Fetching current LinkCheck pricing…
        </p>

        <template v-else>
          <p v-if="creditModalErrorLC" class="error-text">
            {{ creditModalErrorLC }}
          </p>

          <template v-else-if="creditPricingLC">
            <p>
              This LinkCheck run will consume
              <strong>{{ effectiveLinkcheckCost }}</strong>
              LinkCheck credit<span v-if="effectiveLinkcheckCost !== 1">s</span>.
            </p>

            <p class="muted">
              Your current LinkCheck balance:
              <strong>{{ linkcheckCreditsDisplay }}</strong>.
            </p>

            <p
              class="muted tiny"
              v-if="creditPricingLC.discountCost != null"
              style="margin-top: 4px;"
            >
              A temporary discount is currently active for LinkCheck runs.
            </p>
          </template>

          <p v-else class="muted">
            Could not determine pricing details. Please try again in a moment.
          </p>
        </template>
      </section>

      <footer class="modal__foot">
        <button
          type="button"
          class="btn btn--ghost"
          @click="closeCreditModalLC"
          :disabled="creditModalBusyLC"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn"
          @click="onConfirmCostLC"
          :disabled="
            creditModalBusyLC ||
            !!creditModalErrorLC ||
            !creditPricingLC ||
            effectiveLinkcheckCost === null
          "
        >
          Confirm &amp; Run
        </button>
      </footer>
    </div>
  </div>
</teleport>


  </main>
</template>

<script>
import axios from 'axios';
import AuthService from '@/services/AuthService';
import { looksLikeAddress,
   looksLikeAddressOrWrongInput,
    validateTransactionHash,
     isValidAddress } from '@/utils/linkcheck';
import api from '@/services/api';
export default {
  name: 'LinkCheckTool',
  data() {
    return {
      copyKeyModal: '',   // which field we last copied in the modal
      showToAddressPicker: false,
      toAddressSuggestions: [],
      tokenSymbols: {}, 
      showReportModalLC: false,
reportRequestLC: null,
reportObjLC: null,
useOptions: false,
isSubscriptionUser: false,
isUnlimitedUser: false,

role: null,
subscriptionExpiresAt: null,
subscriptionDays: 0,
  inlineCopy: {}, // key -> true while showing ("<reqId>:<field>")
// for status change notifications
lastStatusesLC: {},
    firstFetchDoneLC: false, // <- used by your code too
         pollErrorCount: 0,
      // form
       deletingIds: {},        // { [id]: true }
    confirmDelete: {},      // { [id]: true } — shows confirm buttons
    confirmTimers: {},      // { [id]: timerId } — auto-cancel after timeout
      txHash: '',
      toAddress: '',
      optionsOpen: false,
      params: {
        maxHops: 4,
        minUsdContinuity: 25,
        timeFrom: '',
        timeTo: '',
      },

addressHelpResults: [],
      // auth + credits
      isLoggedIn: false,
      username: '',
      linkcheckCredits: null,

      canUseNormalMode: false, 
      userRole: '',
      // submit / 
      isSubmittingRequest: false,
      activeRequestId: null,

loading: true,

      // result
      currentResult: null,

      // notifications
      notificationMessage: '',
      notificationType: 'info',
    notifTimer: null,

      // clipboard
      copying: false,
      copied: false,
      copyTimer: null,

   reqLoading: false,
    reqError: '',
    requestsLC: [],
    totalReq: 0,
    reqPage: 1,
    reqLimit: 25,
    onlyLinkcheck: true,     // default filter ON



showAddressHelpPanel: false,

 currentPageInAddressHelpPanel: 1,
    resultsPerPageInAddressHelpPanel: 10,
    addressUsedForAssistance: null,

    // Credit confirmation modal for LinkCheck
    showCreditCostModalLC: false,
    creditModalBusyLC: false,
    creditModalErrorLC: '',
    creditPricingLC: null,
    skipCostConfirmLC: false,
    };
  },
  computed: {
      effectiveLinkcheckCost() {
    if (!this.creditPricingLC) return null;
    const base = Number(this.creditPricingLC.baseCost || 0);
    const disc = this.creditPricingLC.discountCost != null
      ? Number(this.creditPricingLC.discountCost)
      : null;

    if (disc != null && disc >= 0 && disc < base) {
      return disc;
    }
    return base || null;
  },
     confidencePct() {
    const c = Number(this.reportObjLC?.confidence ?? 0);
    return Math.max(0, Math.min(100, Math.round(c * 100)));
  },
  confidenceLevelClass() {
    const c = (this.reportObjLC?.confidence ?? 0);
    if (c >= 0.8) return 'conf--high';
    if (c >= 0.5) return 'conf--mid';
    return 'conf--low';
  },
   verdictExplainer() {
    const v = (this.reportObjLC?.verdict || '').toUpperCase();
    switch (v) {
      case 'YES':   return 'A link was found between the submitted transaction and address.';
      case 'NO':    return 'No link was found in the scanned window.';
      case 'MAYBE': return 'Weak/ambiguous evidence was found; manual review recommended.';
      default:      return 'Result unavailable.';
    }
  },
     paginatedResults() {
    const start = (this.currentPageInAddressHelpPanel - 1) * this.resultsPerPageInAddressHelpPanel;
    return this.addressHelpResults.slice(start, start + this.resultsPerPageInAddressHelpPanel);
  },
     visibleRequests() {
    if (this.onlyLinkcheck) {
      return (this.requestsLC || []).filter(r => (r.mode || '') === 'linkcheck');
    }
    return this.requestsLC || [];
  },



      canSubmit() {
    // must be logged in
    if (!this.isLoggedIn) return false;

    // credits: allow when unknown (null), allow when > 0, block when 0
    if (this.linkcheckCredits === 0) return false;

    // at least one of txHash or toAddress present
    const hasTx = !!this.txHash?.trim();
    const hasAddr = !!this.toAddress?.trim();
    if (!hasTx && !hasAddr) return false;

    // if tx present, it must be a valid blockchain tx hash
    if (hasTx) {
      const ok = !!validateTransactionHash(this.txHash.trim()); // should return truthy on valid
      if (!ok) return false;
    }

    // if address present, it must be valid
    if (hasAddr && !isValidAddress(this.toAddress.trim())) return false;

    return true;
  },


    verdictLabel() {
      const label = this.currentResult?.verdict?.label || '';
      if (label === 'YES_LINK_FOUND' || label === 'YES') return 'YES – Link Found';
      if (label === 'MAYBE') return 'MAYBE – Weak/Heuristic';
      return 'NO – No Link Found';
    },
    verdictClass() {
      const label = this.currentResult?.verdict?.label || '';
      return {
        'badge--yes': label === 'YES_LINK_FOUND' || label === 'YES',
        'badge--maybe': label === 'MAYBE',
        'badge--no': !label || label === 'NO_LINK_FOUND' || label === 'NO',
      };
    },
    isYesVerdict() {
      const label = this.currentResult?.verdict?.label || '';
      return label === 'YES_LINK_FOUND' || label === 'YES' || label === 'MAYBE';
    },
    isNoVerdict() {
      const label = this.currentResult?.verdict?.label || '';
      return !label || label === 'NO_LINK_FOUND' || label === 'NO';
    },
 
    confidenceDisplay() {
      return (this.confidencePct / 100).toFixed(2);
    },
    signalsOrPaths() {
      if (!this.currentResult) return [];
      if (this.isYesVerdict) {
        // Top paths: normalize a simple view (hop sequences flattened)
        const paths = this.currentResult.paths || [];
        return paths.map(p => ({
          pathChain: p.chain || p[0]?.chain || '—',
          hopLabel: p.hops?.map((h, i) => `${i + 1}:${this.shorten(h.txid)}`).join(' → ') || '—',
          asset: p.asset || p.hops?.[p.hops.length - 1]?.asset || '—',
          amountUsd: p.amountUsd || null,
          timestamp: p.hops?.[0]?.timestamp || null,
          note: p.note || ''
        }));
      }
      return this.currentResult.nearestSignals || [];
    },
    linkcheckCreditsDisplay() {
      return this.linkcheckCredits === null ? '—' : this.linkcheckCredits;
    },
  },

  
  created() {
  const token = localStorage.getItem('token');
  if (!token) {
    this.loading = false;
    return;
  }

  axios.get('/etherscan/get_credits.php', {
    headers: { Authorization: `Bearer ${token}` }
  })
  .then(res => {
    this.loading = false; 
    if (res.data.status === 'success') {
      this.creditBalance = res.data.balance;
    this.subscriptionExpiresAt = res.data.subscription_expires_at;

//  whole day counts as active
const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
if (expires) expires.setHours(0, 0, 0, 0);
const today = new Date(); today.setHours(0, 0, 0, 0);
const isExpired = !expires || expires < today;

this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
this.isUnlimitedUser   = !!res.data.is_unlimited_user;
this.role              = res.data.role;

// Days remaining: prefer server if sane, otherwise derive from expires
let derivedDays = 0;
if (this.isSubscriptionUser && this.subscriptionExpiresAt) {
  const diffMs = new Date(this.subscriptionExpiresAt) - new Date();
  derivedDays = diffMs > 0 ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) : 0;
}

const serverDays = (typeof res.data.subscriptionDays === 'number' && res.data.subscriptionDays >= 0)
  ? res.data.subscriptionDays
  : null;

this.subscriptionDays = serverDays ?? derivedDays;

    } else {
      console.warn("Could not fetch credit/subscription info.");
    }
  })
  .catch(err => {
    this.loading = false; // ✅ Also clear loading if it fails
    console.error("Error fetching credit info:", err);
  });
},
  mounted() {
    this.checkLogin();
    this.refreshCredits();

  this.fetchRequestsLC();
    
   window.addEventListener('keydown', this._onNotifEsc);
    this.pollErrorCount = 0;

this.pollingInterval = setInterval(() => {
  this.fetchRequestsLC().catch(() => {
    this.pollErrorCount++;
    if (this.pollErrorCount > 5) {
      clearInterval(this.pollingInterval);
      this.showNotif('Polling stopped due to repeated errors.', 'error');
    }
  });
}, 5000);
    

 AuthService.getUserRole().then(data => {
    this.userRole = data.role;
    this.canUseNormalMode = data.canUseNormalMode; // Set permission here
  }).catch(error => {
    console.error("Error fetching user role:", error);
    this.notify('Error fetching permissions, please reload.', 'error');
  });
  },
  
  beforeUnmount() {
     window.removeEventListener('keydown', this._onNotifEsc);
  if (this.notifTimer) clearTimeout(this.notifTimer);
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
  methods: {
    async openCreditConfirmModalLC() {
  this.showCreditCostModalLC = true;
  this.creditModalBusyLC = true;
  this.creditModalErrorLC = '';
  this.creditPricingLC = null;

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const res = await axios.get('/etherscan/fetch_platform_settings.php', {
      params: {
        context: 'linkcheck',
        mode: 'linkcheck',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = res.data || {};
    if (data.status !== 'success' || !data.pricing) {
      throw new Error(data.message || 'Missing pricing data');
    }

    const p = data.pricing || {};

    const baseCost =
      p.base_cost != null
        ? Number(p.base_cost)
        : p.baseCredits != null
        ? Number(p.baseCredits)
        : 0;

    let discountCost = null;
    if (p.discount_cost != null) {
      discountCost = Number(p.discount_cost);
    } else if (p.discountCredits != null) {
      discountCost = Number(p.discountCredits);
    }

    const serverNowUtc = data.server_now_utc || data.serverNowUtc || null;

    this.creditPricingLC = {
      baseCost,
      discountCost,
      discountLabel: p.discount_label || p.discountLabel || '',
      discountStartsUtc: p.discount_starts_utc || p.discountStartsUtc || null,
      discountEndsUtc: p.discount_ends_utc || p.discountEndsUtc || null,
      serverNowUtc,
    };

    if (!this.creditPricingLC.baseCost) {
      throw new Error('Incomplete pricing response for LinkCheck.');
    }

    // Ignore bogus discount that is not actually cheaper
    if (
      this.creditPricingLC.discountCost != null &&
      this.creditPricingLC.discountCost >= this.creditPricingLC.baseCost
    ) {
      this.creditPricingLC.discountCost = null;
    }

    const effective = this.effectiveLinkcheckCost;

    // Front-end heads-up if user does not have enough LinkCheck credits
    if (
      !this.isUnlimitedUser &&
      !this.isSubscriptionUser &&
      this.linkcheckCredits != null &&
      effective != null &&
      effective > this.linkcheckCredits
    ) {
      this.creditModalErrorLC = `This LinkCheck run requires ${effective} credits, but you only have ${this.linkcheckCredits}.`;
    }
  } catch (err) {
    console.error('Failed to load LinkCheck pricing:', err);
    this.creditModalErrorLC =
      err?.message || 'Could not fetch current LinkCheck credit cost.';
  } finally {
    this.creditModalBusyLC = false;
  }
},

closeCreditModalLC() {
  this.showCreditCostModalLC = false;
  this.creditModalBusyLC = false;
  this.creditModalErrorLC = '';
  this.creditPricingLC = null;
  this.skipCostConfirmLC = false;
},

onConfirmCostLC() {
  if (this.creditModalBusyLC) return;
  if (this.creditModalErrorLC) return;
  if (!this.creditPricingLC || this.effectiveLinkcheckCost === null) return;

  // Mark that the user has confirmed the current cost.
  this.skipCostConfirmLC = true;
  this.showCreditCostModalLC = false;

  // Re-enter the submit pipeline, this time skipping the confirmation gate.
  this.submitLinkCheck();
},

     copyText(text, key) {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    this.copyKeyModal = key;
    setTimeout(() => (this.copyKeyModal = ''), 1500);
  },
    humanTimeWindow(fromIso, toIso) {
    const from = new Date(fromIso);
    const to   = new Date(toIso);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return '—';
    let ms = Math.abs(to - from);
    // clamp negatives
    if (ms <= 0) return '0 minutes';

    const sec = Math.floor(ms / 1000);
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);

    const parts = [];
    if (d) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
    if (h && parts.length < 2) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
    if (!d && !h) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
    return parts.join(' ');
  },
  isCopiedKey(key) {
    return this.copyKeyModal === key;
  },
    formatDateLocal(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleString();
},
    getExplorerUrlForAddress(address, network) {
  switch (network) {
    case 'bitcoin':
      return `https://mempool.space/address/${address}`;
    case 'ethereum':
      return `https://etherscan.io/address/${address}`;
    case 'tron':
      return `https://tronscan.org/#/address/${address}`;
    default:
      return '';
  }
}
,
    selectSuggestedTransaction(tx) {
  this.txHash = tx.hash;
  this.toAddress = tx.to;
  
  this.showAddressHelpPanel = false;
  this.showNotif('Transaction selected. You may now submit the request.', 'success');
}
,
    chooseToAddress(address) {
  this.toAddress = address;
  this.showToAddressPicker = false;
},
    formatValue(value, network = '') {
  const float = parseFloat(value);
  if (isNaN(float)) return value;

  if (network.toLowerCase() === 'ethereum') {
    const eth = float / 1e18;
    return `${eth.toLocaleString(undefined, {
      minimumFractionDigits: 6,
      maximumFractionDigits: 8
    })} ETH`;
  }

  if (network.toLowerCase() === 'bitcoin') {
    const btc = float / 1e8;
    return `${btc.toLocaleString(undefined, {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8
    })} BTC`;
  }

  return float.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8
  });
}

,
  isLinkcheckRow(r) {
    return (r.mode || '') === 'linkcheck';
  },
  canOpenReport(r) {
    // Only show "View" for non-greyed LinkCheck rows that have finished
    if (!this.isLinkcheckRow(r)) return false;
    if (r.status !== 'complete') return false;
    try {
      return !!this.parseLcResult(r.result);
    } catch (e) {
      return false;
    }
  },
   keyFor(id, field) { return `${id}:${field}`; },
  isCopied(id, field) { return !!this.inlineCopy[this.keyFor(id, field)]; },

  async copyField(id, field, text) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // simple fallback
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta);
        ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      const k = this.keyFor(id, field);
      if (this.$set) this.$set(this.inlineCopy, k, true); else this.inlineCopy[k] = true;
      setTimeout(() => {
        if (this.$delete) this.$delete(this.inlineCopy, k); else delete this.inlineCopy[k];
      }, 1200);
    } catch (e) {
      // optional: show a tiny inline error instead of the global notif
      console.warn('Copy failed:', e);
    }
  },

parseLcResult(raw) {
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch { return null; }
},
// Normalize any historical labels to YES / NO / MAYBE
normalizeVerdict(raw) {
  const s = String(raw ?? '').trim().toUpperCase();
  if (!s) return null;

  // YES synonyms
  if ([
    'YES','MATCH','MATCH FOUND','LINK','LINK FOUND','LINKED','RELATED','TRUE'
  ].includes(s)) return 'YES';

  // NO synonyms
  if ([
    'NO','NO MATCH','NO LINK','NOT LINKED','UNRELATED','FALSE'
  ].includes(s)) return 'NO';

  // MAYBE / weak
  if ([
    'MAYBE','POSSIBLE','AMBIGUOUS','WEAK','INCONCLUSIVE'
  ].includes(s)) return 'MAYBE';

  return null;
},
getLcVerdict(r) {
  if (r?.status !== 'complete' || !r?.result) return null;
  const obj = this.parseLcResult(r.result);
  if (!obj) return null;

  // prefer explicit fields if present
  const raw = obj.verdict ?? obj.verdictLabel ?? obj.label ?? obj.result ?? null;
  let v = this.normalizeVerdict(raw);

  // Fallbacks for boolean-y payloads
  if (!v) {
    if (obj.match === true || obj.linkFound === true) v = 'YES';
    else if (obj.match === false || obj.linkFound === false) v = 'NO';
  }

  return v; // 'YES' | 'NO' | 'MAYBE' | null
},
verdictTitle(r) {
  const obj = this.parseLcResult(r.result);
  const c = obj?.confidence;
  // Percent tends to be more scannable in a tooltip
  return typeof c === 'number' ? `Confidence: ${(c * 100).toFixed(0)}%` : '';
},
verdictPillClass(v) {
  const n = this.normalizeVerdict(v);
  return {
    'pill--yes': n === 'YES',
    'pill--maybe': n === 'MAYBE',
    'pill--warn': n === 'NO'
  };
},
  reportLocalTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso); // fallback if not parseable
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  },

openReportLC(row) {
  if (row.status !== 'complete') return;
  const obj = this.parseLcResult(row.result);
  if (!obj) {
    this.notify('Unable to read report JSON.', 'error');
    return;
  }
  this.reportRequestLC = row;
  this.reportObjLC = obj;
  this.showReportModalLC = true;
},
closeReportLC() {
  this.showReportModalLC = false;
  this.reportRequestLC = null;
  this.reportObjLC = null;
},

// keep your existing mapping
displayMode(m) {
  if (m === 'linkcheck') return 'linkcheck';
  if (m === 'bucket')    return 'normal';
  if (m === 'normal')    return 'advanced';
  return m || '—';
},


    showNotif(msg, type = 'success', timeoutMs = 5000) {
    this.notificationMessage = msg;
    this.notificationType = type;
    if (this.notifTimer) clearTimeout(this.notifTimer);
    if (timeoutMs && timeoutMs > 0) {
      this.notifTimer = setTimeout(() => this.dismissNotif(), timeoutMs);
    }
  },
  dismissNotif() {
    this.notificationMessage = '';
    if (this.notifTimer) { clearTimeout(this.notifTimer); this.notifTimer = null; }
  },
  _onNotifEsc(e) {
    if (e.key === 'Escape' && this.notificationMessage) this.dismissNotif();
  },

async fetchRequestsLC(opts = {}) {
  const { silent = false } = opts;
  const token = localStorage.getItem('token');
  const username = this.username;
  if (!token || !username) return;

  this.reqLoading = !this.firstFetchDoneLC && !silent;
  this.reqError = '';

  try {
    const params = { username, page: this.reqPage, limit: this.reqLimit };
    if (this.onlyLinkcheck) params.mode_only = 'linkcheck';

    const res = await axios.get('/linkcheck/fetch_requests.php', {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res.data?.status === 'success') {
      const list = res.data.requests || [];

      // notify on status changes (skip very first)
      if (this.firstFetchDoneLC && !silent) {
        for (const r of list) {
          const prev = this.lastStatusesLC[r.request_id];
          if (prev && prev !== r.status) {
            this.notify(`Request ${r.request_id} status updated to ${r.status}.`, 'info');
          }
        }
      }

      // track latest statuses
      for (const r of list) {
        // ensure the object is initialized
        if (!this.lastStatusesLC) this.lastStatusesLC = {};
        this.lastStatusesLC[r.request_id] = r.status;
      }

      this.requestsLC = list;
      this.totalReq   = Number(res.data.total || 0);
      this.firstFetchDoneLC = true;
    } else {
      this.reqError = res.data?.message || 'Failed to load requests.';
    }
  } catch (e) {
    console.error('fetchRequestsLC error', e);
    if (e?.response?.status === 401) {
      this.notify('Your session has expired. Please log in again.', 'error');
      localStorage.removeItem('token');
      this.$router.push('/login');
      return;
    }
    this.reqError = 'Error fetching requests.';
  } finally {
    this.reqLoading = false;
  }
},

  // --- safer delete flow
  askDeleteLC(id) {
    // show confirm, auto-hide after 6s
    this.confirmDelete = { ...this.confirmDelete, [id]: true };
    if (this.confirmTimers[id]) clearTimeout(this.confirmTimers[id]);
    this.confirmTimers[id] = setTimeout(() => {
      this.cancelDeleteLC(id);
    }, 6000);
  },
  cancelDeleteLC(id) {
    if (this.confirmTimers[id]) {
      clearTimeout(this.confirmTimers[id]);
      delete this.confirmTimers[id];
    }
    const next = { ...this.confirmDelete };
    delete next[id];
    this.confirmDelete = next;
  },
    async deleteRequestLC(requestId) {
    if (!this.confirmDelete[requestId]) {
      // belt & suspenders: require explicit confirm state
      this.notify('Please confirm delete.', 'warn');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      this.notify('Please log in again.', 'error');
      return;
    }

    this.deletingIds = { ...this.deletingIds, [requestId]: true };
    try {
      const res = await axios.post(
        '/etherscan/delete_request.php',
        { request_id: requestId, username: this.username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === 'success') {
        this.notify(`Request ${requestId} deleted.`, 'success');
        // Optimistic remove to feel snappier:
        this.requestsLC = this.requestsLC.filter(r => r.request_id !== requestId);
        // or, if you prefer strict consistency: await this.fetchRequestsLC();
      } else {
        this.notify(res.data?.message || 'Failed to delete request.', 'error');
      }
    } catch (e) {
      console.error('deleteRequestLC error', e);
      this.notify('Error deleting request.', 'error');
    } finally {
      // cleanup state
      this.deletingIds = { ...this.deletingIds, [requestId]: false };
      this.cancelDeleteLC(requestId);
      this.fetchRequestsLC();
    }
  },
  // small helpers used in the table
  shorten(v) { if (!v) return '—'; return v.length > 18 ? `${v.slice(0, 10)}…${v.slice(-6)}` : v; },
  formatDateTidy(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      year:'numeric', month:'short', day:'2-digit', hour:'numeric', minute:'2-digit'
    });
  },

  nextReqPage() {
    if (this.reqPage * this.reqLimit < this.totalReq) {
      this.reqPage += 1;
      this.fetchRequestsLC();
    }
  },
  prevReqPage() {
    if (this.reqPage > 1) {
      this.reqPage -= 1;
      this.fetchRequestsLC();
    }
  },

    // ---------------- Auth & credits ----------------
    checkLogin() {
      const token = localStorage.getItem('token');
      this.isLoggedIn = !!token;
      this.username = localStorage.getItem('username') || '';
      if (!this.isLoggedIn) {
        this.notify('Please log in to use LinkCheck.', 'error');
      }
    },
    refreshCredits() {
  api.get('/linkcheck/get_credits.php')
    .then(res => {
      console.log('get_credits response:', res.status, res.data);
      this.linkcheckCredits = res.data?.status === 'success'
        ? Number(res.data.credits)
        : null;
    })
    .catch(() => { this.linkcheckCredits = null; });
}
,

    
  // ---------------- Submit ----------------
submitLinkCheck() { // submitRequest
  if (this.isSubmittingRequest) return;

  this.clearResult();
  this.toAddress = this.toAddress.trim();
  this.txHash = this.txHash.trim();
  this.isSubmittingRequestRequest = true;
  this.validationError = '';

  if (!this.txHash && !this.toAddress) {
    this.validationError = 'You must enter a transaction hash or an address.';
    this.isSubmittingRequestRequest = false;
    return;
  }

  // Validate To Address format (if provided)
  if (this.toAddress && !isValidAddress(this.toAddress)) {
    this.notify('Invalid to address for detected network type.', 'error');
    this.isSubmittingRequestRequest = false;
    return;
  }

  // Access check: Block if not unlimited, not subscribed, and has 0 credits
  if (!this.isUnlimitedUser && !this.isSubscriptionUser && this.linkcheckCredits === 0) {
    this.notify('You have 0 LinkCheck credits.', 'error');
    this.isSubmittingRequestRequest = false;
    return;
  }

  if (looksLikeAddress(this.txHash)) {
    this.enterAddressAssistanceMode(this.txHash);
    this.isSubmittingRequestRequest = false;
    return;
  }

  // Scenario: user entered an address in the To Address field but left txn hash blank
  if (!this.txHash && this.toAddress && looksLikeAddress(this.toAddress)) {
    this.notify(
      "Looks like you entered an address instead of a transaction hash. Here's what we found.",
      'info'
    );

    this.enterAddressAssistanceMode(this.toAddress);
    this.isSubmittingRequest = false;
    this.isSubmittingRequestRequest = false;
    return;
  }

  if (looksLikeAddressOrWrongInput(this.txHash)) {
    this.notify(
      'This does not appear to be a valid transaction hash. Please provide a raw hash, not an address or URL.',
      'error'
    );
    this.isSubmittingRequest = false;
    this.isSubmittingRequestRequest = false;
    return;
  }

  this.checkLogin();
  if (!this.isLoggedIn) {
    this.notify('Please log in to access this tool.', 'error');
    this.isSubmittingRequest = false;
    this.isSubmittingRequestRequest = false;
    return;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    this.notify('No token found. Please log in again.', 'error');
    this.isSubmittingRequestRequest = false;
    return;
  }

  const hashType = validateTransactionHash(this.txHash);
  if (!hashType) {
    this.validationError =
      'Invalid transaction hash. Please provide a valid Ethereum, Bitcoin, or Tron hash.';
    this.isSubmittingRequest = false;
    this.isSubmittingRequestRequest = false;
    return;
  }

  this.validationError = '';

  // --- NEW: credit confirmation gate ---
  if (!this.skipCostConfirmLC) {
    // We have valid input; open the pricing modal and stop here.
    this.isSubmittingRequest = false;
    this.isSubmittingRequestRequest = false;
    this.openCreditConfirmModalLC();
    return;
  }

  // Second invocation: user already confirmed the cost
  this.skipCostConfirmLC = false;
  this.isSubmittingRequest = true;

  axios
    .post('etherscan/check_to_address_needed.php', {
      transaction_hash: this.txHash.trim(),
      network: hashType,
      to_address: this.toAddress?.trim().toLowerCase() || null,
    })
    .then((response) => {
      // Case 1: No To Address provided, but it's required
      if (response.data.requiresToAddress && !this.toAddress) {
        if (Array.isArray(response.data.addressesFound) && response.data.addressesFound.length > 0) {
          this.toAddressSuggestions = response.data.addressesFound;
          this.showToAddressPicker = true;
          this.isSubmittingRequest = false;
          this.notify(
            `This transaction requires a To Address to proceed (${response.data.reason}). Please choose one below. \n\nPicking the wrong address will lead to incorrect results.\n
Use the amount, token, and address to find your own.`,
            'error'
          );
        } else {
          this.notify(
            `This transaction requires a To Address to proceed (${response.data.reason}).`,
            'error'
          );
          this.isSubmittingRequest = false;
        }
        return;
      }

      // Case 2: To Address was provided but it doesn't match
      if (
        this.toAddress &&
        response.data.requiresToAddress &&
        response.data.matchedTo === false &&
        Array.isArray(response.data.addressesFound) &&
        response.data.addressesFound.length > 0
      ) {
        this.toAddressSuggestions = response.data.addressesFound;
        this.showToAddressPicker = true;
        this.isSubmittingRequest = false;
        this.notify(
          `The provided To Address does not match any known destination for this transaction (${response.data.reason}). Please choose a valid one.`,
          'error'
        );
        return;
      }

      const token2 = localStorage.getItem('token');
      if (!token2) {
        this.notify('No token found. Please log in again.', 'error');
        this.isSubmittingRequest = false;
        return;
      }

      // ETH check
      axios
        .post(
          '/etherscan/check_eth_tx_exists.php',
          { txHash: this.txHash },
          { headers: { Authorization: `Bearer ${token2}` } }
        )
        .then((res) => {
          if (res.data?.status === 'ok' && res.data.exists === true) {
            console.log('✅ Valid ETH');
            this.sendAnalysisRequest();
            return;
          }

          // ETH failed → Try BTC
          axios
            .post(
              '/etherscan/check_btc_tx_exists.php',
              { txHash: this.txHash },
              { headers: { Authorization: `Bearer ${token2}` } }
            )
            .then((res2) => {
              if (res2.data?.status === 'ok' && res2.data.found === true) {
                console.log('✅ Valid BTC');
                this.sendAnalysisRequest();
                return;
              }

              // BTC failed → Try TRON
              axios
                .post(
                  '/etherscan/check_tron_tx_exists.php',
                  { txHash: this.txHash },
                  { headers: { Authorization: `Bearer ${token2}` } }
                )
                .then((res3) => {
                  if (res3.data?.status === 'ok' && res3.data.found === true) {
                    console.log('✅ Valid TRON');
                    this.sendAnalysisRequest();
                    return;
                  }

                  // All three failed
                  this.isSubmittingRequest = false;
                  this.notify(
                    'Transaction not found on Ethereum, Bitcoin, or Tron. Please double-check the hash.',
                    'error'
                  );
                })
                .catch((err3) => {
                  console.error('TRON check error:', err3);
                  this.isSubmittingRequest = false;
                  this.notify(
                    'Error checking Tron transaction. Please try again.',
                    'error'
                  );
                });
            })
            .catch((err2) => {
              console.error('BTC check error:', err2);
              this.isSubmittingRequest = false;
              this.notify(
                'Error checking Bitcoin transaction. Please try again.',
                'error'
              );
            });
        })
        .catch((err) => {
          console.error('ETH check error:', err);
          this.isSubmittingRequest = false;
          this.notify(
            'Error checking Ethereum transaction. Please try again.',
            'error'
          );
        });
    })
    .catch((error) => {
      this.isSubmittingRequest = false;
      console.error('To Address check error:', error);
      this.notify(
        'Unable to determine if To Address is required. Please try again.',
        'error'
      );
    });
},


async enterAddressAssistanceMode(address) {
  this.showNotif("Looking up transactions to this address...", "info");
  this.isSubmittingRequest = true;
console.log("begin lookup for " + address);
  try {
    const res = await fetch("/api/etherscan/lookup_address_for_transaction_candidates.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, limit: 50 })
    });

    const data = await res.json();
    if (Array.isArray(data)) {
      this.addressHelpResults = data;
      this.addressUsedForAssistance = address;
this.currentPageInAddressHelpPanel = 1;
      this.showAddressHelpPanel = true;
      this.inferredNetwork = this.addressHelpResults.length > 0
  ? this.addressHelpResults[0].network
  : null;

console.log("look up supplied " + this.addressHelpResults.length); // it does log this but the table is empty
    } else {
      this.showNotif("Could not find transactions to that address.", "error");
    }
  } catch (e) {
    this.showNotif("Lookup failed.", "error");
  } finally {
    this.isSubmittingRequest = false;
  }
}
,
sendAnalysisRequest() {

  const t = localStorage.getItem('token');
if (!t) {
  this.notify('Please log in to use LinkCheck.', 'error');
  this.isSubmittingRequest = false;
  return;
}


  const requestBody = {
    username: this.username || localStorage.getItem('username'), 
    tx_hash: this.txHash.trim(),
    target_address: (this.toAddress || '').trim(),
    request_type: 'single',
    mode: 'linkcheck',
    params: this.buildParams(),
  };

  this.showToAddressPicker = false;
  this.toAddressSuggestions = [];

  api.post('/linkcheck/create_request.php', requestBody)
    .then(res => {
      console.log('create_request OK', res.status, res.data);
      if (res.data?.status === 'success' && res.data.request_id) {
        this.notify('LinkCheck submitted.', 'success');
        this.activeRequestId = res.data.request_id;
        this.refreshCredits();
      } else {
        this.notify(res.data?.message || 'Submission failed.', 'error');
      }
    })
    .catch(err => {
      const status = err?.response?.status;
      const data   = err?.response?.data;
      console.error('create_request ERR', status, data);
      this.notify(data?.message || 'Submission error. Please try again.', 'error');
    })
    .finally(() => {
      this.isSubmittingRequest = false;
      this.txHash = "";
      this.toAddress = "";
      this.fetchRequestsLC();
    });
}

,

    // ---------------- UI helpers ----------------
    buildParams() {
      const p = {
        maxHops: Number(this.params.maxHops) || 4,
        minUsdContinuity: Number(this.params.minUsdContinuity) || 0,
      };
      // Normalize time window
      if (this.params.timeFrom || this.params.timeTo) {
        p.timeWindow = {
          from: this.params.timeFrom || null,
          to: this.params.timeTo || null,
        };
      }
      return p;
    },
    resetForm() {
      if (this.isSubmittingRequest) return;
      this.txHash = '';
      this.toAddress = '';
      this.params.maxHops = 4;
      this.params.minUsdContinuity = 25;
      this.params.timeFrom = '';
      this.params.timeTo = '';
      this.clearResult();
      this.notify('', 'info');
    },
    clearResult() {
      this.currentResult = null;
      this.activeRequestId = null;
    },
    notify(msg, type = 'info') {
      this.notificationMessage = msg;
      this.notificationType = type;
    },

    fmtUsd(n) {
      const v = Number(n || 0);
      return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    fmtTs(ts) {
      if (!ts) return '—';
      try {
        const d = new Date(ts);
        if (isNaN(d.getTime())) return ts;
        return d.toLocaleString();
      } catch {
        return ts;
      }
    },
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
    },

    // exports
    downloadJSON() {
      if (!this.currentResult) return;
      const blob = new Blob([this.pretty(this.currentResult)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const runId = this.currentResult.runId || 'linkcheck';
      a.href = url;
      a.download = `${runId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    copySummary() {
      if (!this.currentResult || this.copying) return;
      this.copying = true;
      const v = this.currentResult.verdict || {};
      const label = v.label || 'NO_LINK_FOUND';
      const conf = this.isNoVerdict ? (v.nonAssociationConfidence ?? 0) : (v.confidence ?? 0);
      const parts = [
        `Verdict: ${label} (${(Number(conf) || 0).toFixed(2)})`,
        `Tx: ${this.shorten(this.currentResult?.input?.txHash || '')}`,
        `Target: ${this.shorten(this.currentResult?.input?.toAddress || '')}`,
        `Params: hops≤${this.currentResult?.input?.params?.maxHops ?? '-'}, minUsd≥${this.currentResult?.input?.params?.minUsdContinuity ?? 0}`,
      ];
      const text = parts.join('\n');
      navigator.clipboard.writeText(text)
        .then(() => {
          this.copied = true;
          this.copying = false;
          if (this.copyTimer) clearTimeout(this.copyTimer);
          this.copyTimer = setTimeout(() => (this.copied = false), 2000);
        })
        .catch(() => {
          this.copying = false;
          this.notify('Clipboard permission denied.', 'error');
        });
    },
  },
};
</script>
<style scoped>
/* ===== Theming ===== */
:host, .linkcheck {
  --ink: var(--ink, #eaf2ff);
  --ink-muted: var(--ink-muted, #a7b6c9);
  --ink-strong: var(--ink-strong, #ffffff);
  --ink-body: var(--ink-body, #1f2a37);

  --navy-1: var(--navy-1, #0b1b2b);
  --navy-2: var(--navy-2, #0f263d);
  --navy-3: var(--navy-3, #0a1a2a);

  --surface: #0b1320;
  --surface-2: #0f1a2a;
  --surface-3: #0a1624;
  --hairline: #1e2a40;
  --border: #24324b;

  --brand: #2a5bd7;
  --brand-2: #6e9bff;
  --brand-3: #9db2ff;

  --ok: #31d17e;
  --warn: #ffb020;
  --err: #ff5d5d;

  --radius-lg: 14px;
  --radius-md: 10px;
  --radius-sm: 8px;

  --shadow-1: 0 10px 30px rgba(0,0,0,.35);
  --shadow-2: 0 4px 12px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.03);
}

/* ===== Layout ===== */
.linkcheck { max-width: 1040px; margin: 0 auto; padding: 20px; }

/* ===== Card / Panels (glassy gradient) ===== */
.card {
  position: relative;
  color: var(--ink);
  border-radius: var(--radius-lg);
  padding: 18px;
  margin-bottom: 18px;
  background:
    radial-gradient(120% 120% at 0% 0%, rgba(42,91,215,.12) 0%, transparent 55%),
    linear-gradient(180deg, var(--surface-2), var(--surface));
  border: 1px solid var(--hairline);
  box-shadow: var(--shadow-1);
  overflow: hidden;
}
.card::before {
  /* subtle sheen */
  content: "";
  position: absolute; inset: 0;
  background: radial-gradient(70% 60% at 100% 0%, rgba(157,178,255,.07), transparent 60%);
  pointer-events: none;
}
.card--hero {
  padding: 22px 22px;
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(157,178,255,.12) 10%, transparent 60%),
    linear-gradient(180deg, var(--navy-2), var(--navy-3));
  border: 1px solid rgba(157,178,255,.16);
}
.hero { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.hero__text h1 { margin: 0 0 6px 0; font-size: 26px; letter-spacing: .2px; }
.sub { margin: 0; opacity: .85; }


/* ===== Card / Panels (glassy gradient) ===== */
.tool-card {
  position: relative;
  color: var(--ink);
  border-radius: var(--radius-lg);
  padding: 18px;
  margin-bottom: 18px;
  background:
    radial-gradient(120% 120% at 0% 0%, rgba(42,91,215,.12) 0%, transparent 55%),
    linear-gradient(180deg, var(--surface-2), var(--surface));
  border: 1px solid var(--hairline);
  box-shadow: var(--shadow-1);
  overflow: hidden;
  color: rgba(255,255,255,0.9);
}
.tool-card::before {
  /* subtle sheen */
  content: "";
  position: absolute; inset: 0;
  background: radial-gradient(70% 60% at 100% 0%, rgba(157,178,255,.07), transparent 60%);
  pointer-events: none;
}
.tool-card--hero {
  padding: 22px 22px;
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(157,178,255,.12) 10%, transparent 60%),
    linear-gradient(180deg, var(--navy-2), var(--navy-3));
  border: 1px solid rgba(157,178,255,.16);
}

/* ===== Pill (credits) ===== */
.status-right { display: flex; gap: 10px; align-items: center; }
.pill {
  position: relative;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  background: linear-gradient(180deg, rgba(20,35,68,.9), rgba(16,26,44,.9));
  border: 1px solid rgba(157,178,255,.18);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.06);
  color:  rgba(255, 255, 255, 0.9);
}
.pill::before {
  content: "";
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--ok);
  box-shadow: 0 0 10px rgba(49,209,126,.6);
}
.pill--warn::before {
  background: #d86b6b; box-shadow: 0 0 10px rgba(216,107,107,.55);
}

/* ===== Notifications ===== */
.notif { border-left: 4px solid rgba(157,178,255,.25); background: linear-gradient(180deg, rgba(42,91,215,.08), transparent); }
.notif--success { border-color: rgba(49,209,126,.6); }
.notif--error { border-color: rgba(255,93,93,.75); }
/* ===== Notifications ===== */
.notif { 
  position: relative;
  border-left: 4px solid rgba(157,178,255,.25);
  background: linear-gradient(180deg, rgba(42,91,215,.08), transparent);
  padding-right: 2.4rem; /* room for the close button */
}
.notif--success { border-color: rgba(49,209,126,.6); }
.notif--error   { border-color: rgba(255,93,93,.75); }

.notif__text { margin: 0; }

.notif__close {
  position: absolute;
  top: .45rem; right: .5rem;
  border: 0; background: transparent;
  font-size: 1.2rem; line-height: 1;
  cursor: pointer; color: #1f2a37; opacity: .6;
}
.notif__close:hover { opacity: 1; }

/* Simple enter/leave animation */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all .18s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* ===== Form fields ===== */
.field { display: flex; flex-direction: column; margin-bottom: 14px; }
.field label { margin-bottom: 6px; font-weight: 600; color: var(--ink-strong); letter-spacing: .2px; }

.field input[type="text"],
.field input[type="date"],
.field input[type="number"] {
  appearance: none;
  background:
    radial-gradient(120% 100% at 0% 0%, rgba(157,178,255,.08), transparent 60%),
    linear-gradient(180deg, #0b1320, #0a1624);
  border: 1px solid var(--border);
  color: var(--ink);
  border-radius: 10px;
  padding: 12px 14px;
  outline: none;
  transition: border-color .2s ease, box-shadow .2s ease, transform .05s ease;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
}
.field input::placeholder { color: var(--ink-muted); opacity: .75; }
.field input:focus {
  border-color: rgba(109,160,255,.7);
  box-shadow: 0 0 0 3px rgba(42,91,215,.25), inset 0 1px 0 rgba(255,255,255,.06);
}
.field input:disabled { opacity: .6; cursor: not-allowed; }

/* Number/date smalls */
.field.small { max-width: 200px; }

/* ===== Options / details ===== */
.options summary {
  list-style: none; cursor: pointer; padding: 10px 0; font-weight: 700; color: var(--ink);
}
.options summary::marker { display: none; }
.options summary::after {
  content: "▸";
  display: inline-block; margin-left: 8px;
  transition: transform .2s ease;
  opacity: .8;
}
.options[open] summary::after { transform: rotate(90deg); }
.options-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-top: 6px; }
.toggles { display: flex; flex-direction: column; gap: 8px; padding-top: 4px; }
.toggles label { font-weight: 500; color: var(--ink); opacity: .9; }
.toggles input { accent-color: var(--brand); }

/* ===== Buttons ===== */
.actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }

.primary-button {
  --grad-a: #3a6bff;
  --grad-b: #1f4ad6;
  color: white;
  background: linear-gradient(180deg, var(--grad-a), var(--grad-b));
  border: 1px solid rgba(109,160,255,.45);
  padding: 11px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: .2px;
  box-shadow: 0 8px 22px rgba(58,107,255,.22), inset 0 1px 0 rgba(255,255,255,.18);
  transition: transform .08s ease, box-shadow .2s ease, filter .2s ease;
}
.primary-button:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 10px 28px rgba(58,107,255,.28); }
.primary-button:active:not(:disabled) { transform: translateY(0); filter: brightness(.98); }
.primary-button:disabled { opacity: .6; cursor: not-allowed; }

.text-button {
  background: transparent; color: var(--brand-3);
  border: 1px solid transparent; padding: 8px 10px; border-radius: 10px;
  transition: color .15s ease, background .15s ease, border-color .15s ease;
  cursor: pointer;
}
.text-button:hover:not(:disabled) { background: rgba(157,178,255,.08); border-color: rgba(157,178,255,.12); }
.text-button:disabled { opacity: .6; cursor: not-allowed; }

/* ===== Verdict & Confidence ===== */
.verdict-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.badge {
  padding: 7px 12px; border-radius: 999px; font-weight: 800; letter-spacing: .3px;
  border: 1px solid transparent;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
}
.badge--yes { color: #b6ffd0; background: linear-gradient(180deg, rgba(36,76,52,.9), rgba(22,48,35,.9)); border-color: rgba(49,209,126,.45); }
.badge--maybe { color: #fff0b3; background: linear-gradient(180deg, rgba(76,64,28,.9), rgba(54,46,21,.9)); border-color: rgba(255,208,102,.4); }
.badge--no { color: #ffc1c1; background: linear-gradient(180deg, rgba(76,28,28,.9), rgba(54,21,21,.9)); border-color: rgba(255,93,93,.45); }

.confidence { flex: 1; min-width: 240px; }
.bar {
  height: 10px; border-radius: 7px; overflow: hidden;
  background: linear-gradient(180deg, #0a1220, #0a1624);
  border: 1px solid var(--hairline);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
}
.fill {
  height: 100%;
  background: linear-gradient(90deg, #39f 0%, #6cf 100%);
  box-shadow: inset 0 -1px 0 rgba(0,0,0,.15);
  transition: width .35s ease;
}

/* Params recap */
.params-recap p { margin: 10px 0 0 0; color: var(--ink); opacity: .9; }

/* ===== Result grid panels ===== */
.result-grid { display: grid; grid-template-columns: 1.25fr 1fr 1fr; gap: 16px; margin-top: 14px; }
.panel {
  background:
    radial-gradient(100% 120% at 0% 0%, rgba(157,178,255,.06), transparent 60%),
    linear-gradient(180deg, #0a1322, #0b1424);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-md);
  padding: 12px;
  box-shadow: var(--shadow-2);
  transition: border-color .2s ease, box-shadow .2s ease;
}
.panel:hover { border-color: rgba(157,178,255,.22); box-shadow: 0 6px 18px rgba(0,0,0,.28); }
.panel h3 { margin: 0 0 10px 0; }

/* ===== Tables ===== */
table { width: 100%; border-collapse: collapse; border-radius: 10px; overflow: hidden; }
thead th {
  position: sticky; top: 0; z-index: 1;
  background: linear-gradient(180deg, rgba(20,32,54,.9), rgba(12,22,36,.9));
  border-bottom: 1px solid var(--hairline);
  font-weight: 700; letter-spacing: .2px; font-size: 13px;
  padding: 10px 8px;
}
tbody td { padding: 10px 8px; border-bottom: 1px solid rgba(255,255,255,.04); }
tbody tr:nth-child(odd) { background: linear-gradient(180deg, rgba(157,178,255,.03), transparent); }
tbody tr:hover { background: linear-gradient(180deg, rgba(109,160,255,.08), rgba(109,160,255,.02)); }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; font-size: 12.5px; }
.muted { opacity: .78; }
.warn { color: #ffb0b0; }

/* dim non-LinkCheck rows for context */
.row-dim { opacity: .55; }
.ph-table .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }


/* ===== Raw JSON ===== */
.raw summary { cursor: pointer; padding: 8px 0; font-weight: 700; }
.small-pre {
  max-height: 340px; overflow: auto; background: #0b1320;
  border: 1px solid var(--hairline); border-radius: 10px; padding: 12px;
}
.btn--danger { background:#d9534f; color:#fff; }
.btn--danger:hover { background:#c9302c; }

/* ===== Spinner ===== */
.spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid var(--brand); border-top-color: transparent;
  border-radius: 50%; animation: spin 1s linear infinite; margin-left: 8px; opacity: .9;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== Responsive ===== */
@media (max-width: 1040px) {
  .result-grid { grid-template-columns: 1fr; }
  .options-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 560px) {
  .options-grid { grid-template-columns: 1fr; }
}

/* ===== Reduced motion friendly ===== */
@media (prefers-reduced-motion: reduce) {
  .primary-button, .fill { transition: none; }
  .spinner { animation: none; }
}

.pill--yes { background:#e6ffed; color:#0f5132; border:1px solid #badbcc; }
.pill--maybe { background:#fff7e6; color:#664d03; border:1px solid #ffecb5; }
.pill--warn { background:#ffe6e6; color:#842029; border:1px solid #f5c2c7; }



.report-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .55);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Go above literally everything */
  z-index: 2147483647; /* max 32-bit int */
}

/* Base panel */
.modal.modal--report {
  width: 95%;
  max-width: 900px;
  max-height: 80vh;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(157,178,255,.18);
  box-shadow: 0 10px 30px rgba(0,0,0,.45);
  background:
    radial-gradient(100% 120% at 0% 0%, rgba(157,178,255,.06), transparent 60%),
    linear-gradient(180deg, #0a1322, #0b1424);
  color: #eaf2ff; /* force light text in the whole modal */
}

/* Header */
.modal.modal--report .modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(157,178,255,.18);
  background: rgba(255,255,255,.04);
  color: #eaf2ff;
}
.modal.modal--report .modal__head .close {
  border: 0;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #eaf2ff;
  opacity: .85;
  cursor: pointer;
}

/* Body */
.modal.modal--report .modal__body {
  padding: 14px;
  max-height: calc(80vh - 54px);
  overflow: auto;
  color: #eaf2ff;
}

/* Ensure all text inside is readable even if site CSS sets dark colors */
.modal.modal--report .report__meta,
.modal.modal--report .report__body,
.modal.modal--report h4,
.modal.modal--report p,
.modal.modal--report li,
.modal.modal--report table,
.modal.modal--report th,
.modal.modal--report td,
.modal.modal--report .label,
.modal.modal--report .value,
.modal.modal--report .mono,
.modal.modal--report .truncate {
  color: #eaf2ff;
}

/* Tables inside modal */
.modal.modal--report table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 10px;
  overflow: hidden;
}
.modal.modal--report thead th {
  background: linear-gradient(180deg, rgba(20,32,54,.9), rgba(12,22,36,.9));
  border-bottom: 1px solid rgba(157,178,255,.18);
  font-weight: 700;
  padding: 10px 8px;
}
.modal.modal--report tbody td {
  border-bottom: 1px solid rgba(255,255,255,.08);
  padding: 10px 8px;
}

/* Utility inside modal */
.modal.modal--report .mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
}
.modal.modal--report .truncate {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  display: inline-block; max-width: 540px;
}

/* Pills in modal should stay readable */
.modal.modal--report .pill { color: #0f5132; background: #e6ffed; border:1px solid #badbcc; }
.modal.modal--report .pill--maybe { color:#664d03; background:#fff7e6; border-color:#ffecb5; }
.modal.modal--report .pill--warn  { color:#842029; background:#ffe6e6; border-color:#f5c2c7; }
/* Reusable truncation that shows the full content on hover via title attr */
.truncate {
  display: inline-block;
  max-width: 240px;           /* tweak to taste */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/* Make selecting/copying painless */
.copyall {
  user-select: all;
  -webkit-user-select: all;
}

/* If you want a bit wider in tables only */
.ph-table .txcell .truncate { max-width: 260px; }
.txcell { position: relative; }
.copy-btn.tiny {
  margin-left: 6px;
  opacity: .7;
  border: 0; background: transparent; color: var(--brand-3);
  cursor: pointer;
}
.copy-btn.tiny:hover { opacity: 1; text-decoration: underline; }
.txcell .truncate {
  display: inline-block;
  max-width: 260px; /* tweak */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.copy-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}

.copy-btn.tiny {
  border: 0;
  background: transparent;
  color: var(--brand-3);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  opacity: .8;
}
.copy-btn.tiny:hover { opacity: 1; text-decoration: underline; }

.copy-inline {
  font-size: 12px;
  color: var(--ok); /* falls back to your green */
  opacity: .9;
}

/* tiny fade */
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }


/* Panel Modals */
.panelmodal {
  max-width: 720px; width: 100%;
  margin: 1rem auto; background: #fff; border: 1px solid var(--border);
  border-radius: 12px; box-shadow: 0 10px 24px rgba(16,24,40,.14);
  overflow: hidden;
}
.panelmodal__head {
  display: flex; justify-content: space-between; align-items: center;
  padding: .9rem 1rem; background: #f7f9fc; border-bottom: 1px solid var(--border);
}
.panelmodal__body { max-height: 60vh; overflow-y: auto; padding: .75rem 1rem; }
/* Report modal tweaks */
.panelmodal--report { max-width: 760px; }

/* Your modal content scrolls internally if tall */
.panelmodal--report {
  max-width: 760px;
  width: calc(100% - 32px);
  max-height: min(85vh, 900px);
  overflow: auto;               /* only the modal scrolls, page remains as-is */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
/* Full-viewport layer that darkens/blur background */
.panelmodal-overlay.modal-overlay--floating {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(1px);
  pointer-events: none;   /* lets page still scroll */
  z-index: 9999;
}

/* Dialog itself (your existing spacing rules) */
.panelmodal {
  pointer-events: auto;   /* modal still interactive */
  width: min(560px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: var(--card-bg, #fff);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  outline: none;
}


/* Head */
.panelmodal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
}
.panelmodal__head .close {
  margin-left: 12px;
}

/* Body */
.panelmodal__body {
  padding: 16px 18px;
}
.panelmodal__body p:last-child { margin-bottom: 0; }

/* Actions */
.panelmodal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;                 /* space between buttons */
  padding: 14px 18px;
  border-top: 1px solid rgba(0,0,0,0.07);
}

/* Buttons (contrast fix you added) */
.panelmodal .btn.btn--primary {
  background-color: #2d6cdf;
  color: #fff;
  border: none;
}
.panelmodal .btn.btn--primary:hover { background-color: #1e4fa3; }
.panelmodal .btn.btn--ghost { background: transparent; color: #333; }


/* ===== Report redesign ===== */
.lc-report { display: grid; gap: 16px; }

/* HERO */
.report__hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 14px;
  border: 1px solid rgba(157,178,255,.18);
  border-radius: 12px;
  background:
    radial-gradient(100% 140% at 0% 0%, rgba(157,178,255,.10), transparent 60%),
    linear-gradient(180deg, rgba(20,32,54,.75), rgba(12,22,36,.75));
}
.report__hero .hero-left .hero-line { display: flex; gap: 10px; align-items: center; }
.report__hero .mode-badge {
  padding: 4px 10px; border-radius: 999px; font-size: 12px;
  background: rgba(255,255,255,.06); border: 1px solid rgba(157,178,255,.18);
}
.report__hero .hero-sub { margin-top: 6px; opacity: .85; }

.report__hero .hero-right { min-width: 200px; text-align: right; }
.conf-score { font-size: 28px; font-weight: 900; letter-spacing: .3px; }
.meter {
  height: 10px; border-radius: 999px; overflow: hidden;
  background: linear-gradient(180deg, #0b1320, #0a1624);
  border: 1px solid var(--hairline);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.05);
  margin-top: 6px;
}
.meter__fill { height: 100%; width: 0; transition: width .35s ease; }
.conf-caption { font-size: 12px; opacity: .8; margin-top: 4px; }

/* Confidence themes */
.conf--high .conf-score { color: #9ff4c4; }
.conf--mid  .conf-score { color: #ffe7a6; }
.conf--low  .conf-score { color: #ffb3b3; }

.conf--high .meter__fill { background: linear-gradient(90deg,#31d17e,#8fe7b8); }
.conf--mid  .meter__fill { background: linear-gradient(90deg,#ffb020,#ffd580); }
.conf--low  .meter__fill { background: linear-gradient(90deg,#ff5d5d,#ff9a9a); }

/* META GRID */
.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 12px;
  border: 1px solid rgba(157,178,255,.12);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(20,32,54,.45), rgba(12,22,36,.45));
}
.meta-grid label {
  display: block; font-size: 12px; letter-spacing: .3px; opacity: .85;
  margin-bottom: 4px; text-transform: uppercase;
}
.meta-grid .value { line-height: 1.35; }
.meta-grid .copywrap { display: inline-flex; align-items: center; gap: 8px; }
.copy-link {
  background: transparent; border: 0; color: var(--brand-3);
  cursor: pointer; font-size: 12px; opacity: .85; padding: 0;
}
.copy-link:hover { opacity: 1; text-decoration: underline; }
.copied-ok { font-size: 12px; color: var(--ok); opacity: .9; }

/* STATS */
.stat-cards {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
}
.stat {
  border: 1px solid rgba(157,178,255,.12);
  border-radius: 12px; padding: 12px;
  background: linear-gradient(180deg, rgba(20,32,54,.45), rgba(12,22,36,.45));
}
.stat__num { font-size: 20px; font-weight: 800; }
.stat__label { font-size: 12px; opacity: .85; margin-top: 4px; }
.stat--quality { grid-column: span 2; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.chip {
  font-size: 12px; padding: 4px 8px; border-radius: 999px;
  background: rgba(255,255,255,.06); border: 1px solid rgba(157,178,255,.18);
}
.chip--warn { background: rgba(255,176,32,.15); border-color: rgba(255,176,32,.35); }
.chip--err  { background: rgba(255,93,93,.18);  border-color: rgba(255,93,93,.45); }

/* SAMPLES spacing */
.sample { margin-top: 6px; }
.sample h4 { margin: 8px 0; }

/* Truncation utilities already present; reuse .truncate/.mono */
/* tiny utility (if not already defined) */
.tiny { font-size: 12px; }

/* verdict explainer line under the pill */
.hero-note {
  margin-top: 4px;
  font-size: 13px;
  opacity: .95;
}


/* Bigger, more legible inputs just for these fields */
.field--xl input {
  font-size: 16.5px;
  line-height: 1.25;
  padding: 14px 16px;
  border-radius: 12px;
}
.field--mono input,
.field--mono .input {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-variant-numeric: tabular-nums;
  letter-spacing: .2px;
}

/* Affixed icon/button pattern */
.input-affix { position: relative; }
.input--affixed { padding-left: 42px; padding-right: 86px; }
.prefix-icon {
  position: absolute;
  left: 12px; top: 50%;
  transform: translateY(-50%);
  width: 18px; height: 18px;
  color: var(--brand-3);
  opacity: .9;
  pointer-events: none;
}
.suffix-btn {
  position: absolute;
  right: 8px; top: 50%;
  transform: translateY(-50%);
  height: 28px; line-height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid rgba(157,178,255,.18);
  background: linear-gradient(180deg, rgba(20,35,68,.6), rgba(16,26,44,.6));
  color: var(--brand-3);
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  transition: filter .15s ease, transform .05s ease;
}
.suffix-btn:hover { filter: brightness(1.05); }
.suffix-btn:active { transform: translateY(-50%) scale(.98); }

/* Subtext below the input */
.hint {
  margin: 6px 2px 0 2px;
  font-size: 12.5px;
  color: var(--ink-muted);
}

/* Subtle state classes you can toggle later (optional) */
.input--ok    { border-color: rgba(49,209,126,.6) !important; box-shadow: 0 0 0 3px rgba(49,209,126,.18) !important; }
.input--warn  { border-color: rgba(255,176,32,.65) !important; box-shadow: 0 0 0 3px rgba(255,176,32,.18) !important; }
.input--error { border-color: rgba(255,93,93,.8) !important;  box-shadow: 0 0 0 3px rgba(255,93,93,.20) !important; }
/* Hash a bit wider than Address */
.linkcheck-form .form-grid-2 {
  display: grid;
  grid-template-columns:
    minmax(520px, 1.85fr)  /* Transaction Hash */
    minmax(320px, 1.15fr); /* To Address      */
  gap: 14px;
}

/* Tweak ratio at large/tablet widths if you like */
@media (max-width: 1200px) {
  .linkcheck-form .form-grid-2 {
    grid-template-columns: minmax(380px, 1.7fr) minmax(300px, 1.1fr);
  }
}

@media (max-width: 860px) {
  .linkcheck-form .form-grid-2 { grid-template-columns: 1fr; } /* stack */
}
.report-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
/* Center and dim the overlay for this modal */
.credit-modal-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.7); /* dark glass overlay */
  z-index: 9999;
}

/* Panel styling specifically for the LinkCheck credit modal */
.credit-modal {
  background: #0b1020;                /* dark panel */
  color: #e5e7eb;                     /* light text */
  border-radius: 8px;
  max-width: 480px;
  width: calc(100% - 32px);
  padding: 16px 20px 18px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.6);
}

/* Head / body / foot layout */
.credit-modal .modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.35); /* slate-400-ish */
}

.credit-modal .modal__body {
  font-size: 0.95rem;
  line-height: 1.5;
}

.credit-modal .modal__body p {
  margin: 0 0 8px;
}

.credit-modal .modal__foot {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Buttons inside this modal */
.credit-modal .btn {
  background: #6366f1;        /* indigo */
  border-color: #6366f1;
  color: #f9fafb;
}

.credit-modal .btn:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
}

.credit-modal .btn.btn--ghost {
  background: transparent;
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.8);
}

.credit-modal .btn.btn--ghost:hover:not(:disabled) {
  background: rgba(148, 163, 184, 0.18);
}

/* Close “×” button in the header */
.credit-modal .close {
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.credit-modal .close:hover {
  color: #e5e7eb;
}

/* Text helpers inside the modal */
.credit-modal .error-text {
  color: #fecaca; /* light-red on dark */
}

.credit-modal .muted {
  color: #9ca3af;
}

.credit-modal .muted.tiny {
  font-size: 0.75rem;
}


</style>
